/**
 * Mobile Enhancements - モバイルタッチインタラクション強化
 * 
 * 和菓子・食品EC向けShopifyテーマ
 * モバイルユーザー体験を最適化するためのスクリプト
 */

class MobileEnhancements {
  constructor() {
    this.init();
  }

  init() {
    // タッチデバイスの検出
    this.isTouchDevice = 'ontouchstart' in window || navigator.maxTouchPoints > 0;
    
    if (this.isTouchDevice) {
      this.setupTouchInteractions();
      this.optimizeTapTargets();
      this.setupSwipeGestures();
      this.improveScrollPerformance();
    }
  }

  /**
   * タッチインタラクションの設定
   */
  setupTouchInteractions() {
    // カード要素へのタッチフィードバック
    const cards = document.querySelectorAll('.card, .product-card, .gift-scene-card');
    
    cards.forEach(card => {
      card.addEventListener('touchstart', function() {
        this.style.transform = 'scale(0.98)';
      }, { passive: true });

      card.addEventListener('touchend', function() {
        this.style.transform = '';
      }, { passive: true });
    });

    // ボタンのタッチフィードバック
    const buttons = document.querySelectorAll('.button, button, a[role="button"]');
    
    buttons.forEach(button => {
      button.addEventListener('touchstart', function(e) {
        this.classList.add('button--touched');
      }, { passive: true });

      button.addEventListener('touchend', function() {
        setTimeout(() => {
          this.classList.remove('button--touched');
        }, 300);
      }, { passive: true });
    });
  }

  /**
   * タップターゲットの最適化
   * iOS/Androidガイドラインに準拠（最小44x44px）
   */
  optimizeTapTargets() {
    const minSize = 44; // iOS推奨サイズ
    const smallTargets = document.querySelectorAll('a, button, input[type="checkbox"], input[type="radio"]');

    smallTargets.forEach(target => {
      const rect = target.getBoundingClientRect();
      
      if (rect.width < minSize || rect.height < minSize) {
        target.style.minWidth = `${minSize}px`;
        target.style.minHeight = `${minSize}px`;
        target.style.display = 'inline-flex';
        target.style.alignItems = 'center';
        target.style.justifyContent = 'center';
      }
    });
  }

  /**
   * スワイプジェスチャーのサポート
   */
  setupSwipeGestures() {
    const swipeableElements = document.querySelectorAll('[data-swipeable]');
    
    swipeableElements.forEach(element => {
      let touchStartX = 0;
      let touchEndX = 0;
      let touchStartY = 0;
      let touchEndY = 0;

      element.addEventListener('touchstart', (e) => {
        touchStartX = e.changedTouches[0].screenX;
        touchStartY = e.changedTouches[0].screenY;
      }, { passive: true });

      element.addEventListener('touchend', (e) => {
        touchEndX = e.changedTouches[0].screenX;
        touchEndY = e.changedTouches[0].screenY;
        this.handleSwipe(element, touchStartX, touchEndX, touchStartY, touchEndY);
      }, { passive: true });
    });
  }

  handleSwipe(element, startX, endX, startY, endY) {
    const diffX = startX - endX;
    const diffY = startY - endY;
    const threshold = 50; // スワイプと認識する最小距離

    // 横方向のスワイプが縦方向より大きい場合
    if (Math.abs(diffX) > Math.abs(diffY)) {
      if (Math.abs(diffX) > threshold) {
        if (diffX > 0) {
          // 左スワイプ
          element.dispatchEvent(new CustomEvent('swipeLeft'));
        } else {
          // 右スワイプ
          element.dispatchEvent(new CustomEvent('swipeRight'));
        }
      }
    }
  }

  /**
   * スクロールパフォーマンスの改善
   */
  improveScrollPerformance() {
    // Passive event listenersの適用
    let lastScrollY = window.scrollY;
    let ticking = false;

    window.addEventListener('scroll', () => {
      lastScrollY = window.scrollY;

      if (!ticking) {
        window.requestAnimationFrame(() => {
          this.handleScroll(lastScrollY);
          ticking = false;
        });

        ticking = true;
      }
    }, { passive: true });
  }

  handleScroll(scrollY) {
    // ヘッダーの固定/非固定切り替え
    const header = document.querySelector('.header, header');
    
    if (header && scrollY > 100) {
      header.classList.add('header--scrolled');
    } else if (header) {
      header.classList.remove('header--scrolled');
    }
  }
}

// 画像の遅延読み込み最適化
class LazyLoadOptimizer {
  constructor() {
    this.setupIntersectionObserver();
  }

  setupIntersectionObserver() {
    if ('IntersectionObserver' in window) {
      const imageObserver = new IntersectionObserver((entries, observer) => {
        entries.forEach(entry => {
          if (entry.isIntersecting) {
            const img = entry.target;
            
            if (img.dataset.src) {
              img.src = img.dataset.src;
              img.removeAttribute('data-src');
            }
            
            if (img.dataset.srcset) {
              img.srcset = img.dataset.srcset;
              img.removeAttribute('data-srcset');
            }
            
            observer.unobserve(img);
          }
        });
      }, {
        rootMargin: '50px 0px',
        threshold: 0.01
      });

      // 遅延読み込み対象の画像を監視
      document.querySelectorAll('img[data-src], img[data-srcset]').forEach(img => {
        imageObserver.observe(img);
      });
    }
  }
}

// フォームのバリデーション強化
class MobileFormEnhancements {
  constructor() {
    this.enhanceForms();
  }

  enhanceForms() {
    const forms = document.querySelectorAll('form');
    
    forms.forEach(form => {
      // 入力中のフィールドを目立たせる
      const inputs = form.querySelectorAll('input, textarea, select');
      
      inputs.forEach(input => {
        input.addEventListener('focus', function() {
          this.closest('.form-group, .field')?.classList.add('field--focused');
        });

        input.addEventListener('blur', function() {
          this.closest('.form-group, .field')?.classList.remove('field--focused');
        });

        // リアルタイムバリデーションフィードバック
        if (input.hasAttribute('required')) {
          input.addEventListener('blur', function() {
            if (!this.value.trim()) {
              this.classList.add('input--error');
              this.setAttribute('aria-invalid', 'true');
            } else {
              this.classList.remove('input--error');
              this.removeAttribute('aria-invalid');
            }
          });
        }
      });
    });
  }
}

// 初期化
document.addEventListener('DOMContentLoaded', () => {
  new MobileEnhancements();
  new LazyLoadOptimizer();
  new MobileFormEnhancements();
});

// カート追加時のフィードバック改善
document.addEventListener('shopify:cart:add', (event) => {
  // モバイルでカート追加成功時の視覚的フィードバック
  const button = event.target?.closest('button, .button');
  
  if (button) {
    button.classList.add('button--success');
    
    // アイコンを一時的に変更
    const originalHTML = button.innerHTML;
    button.innerHTML = '✓ カートに追加されました';
    
    setTimeout(() => {
      button.innerHTML = originalHTML;
      button.classList.remove('button--success');
    }, 2000);
  }
});
